// AiActionReport.js

import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Flex,
  Text,
  VStack,
  HStack,
  Spinner,
  Select,
  Button,
  useTheme,
  useColorMode,
} from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
  faMagnifyingGlassDollar,
} from "@fortawesome/free-solid-svg-icons";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { cqAPI } from "../../lib/Auth";

function AiProductionReport() {
  const [actionsReport, setActionsReport] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedRange, setSelectedRange] = useState("Today");
  const [sortConfig, setSortConfig] = useState({
    key: "created",
    direction: "descending", // newest actions first
  });
  const [dateRange, setDateRange] = useState([
    moment().startOf("day").toDate(),
    moment().endOf("day").toDate(),
  ]);

  const [startDate, endDate] = dateRange;
  const theme = useTheme();
  const { colorMode } = useColorMode();

  const fetchActions = async (params = {}) => {
    const { startDate: sd, endDate: ed } = params;
    setIsLoading(true);

    try {
      const formattedStartDate = sd
        ? moment(sd).format("L")
        : moment(dateRange[0]).format("L");
      const formattedEndDate = ed
        ? moment(ed).format("L")
        : moment(dateRange[1]).format("L");

      const response = await cqAPI.get(
        `/reports/ai-revenue?rangeBeg=${formattedStartDate}&rangeEnd=${formattedEndDate}`,
        {},
        0
      );

      if (response.type === "ok") {
        // data: { first_name, last_name, planned, completed, starttime, patient_guid, created, type, action }
        setActionsReport(response.data || []);
      }
    } catch (error) {
      console.error("Error fetching AI actions:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // On mount – initialise from URL hash (shareable reports)
    const hashParams = new URLSearchParams(window.location.hash.slice(1));
    const sd = hashParams.get("startDate")
      ? moment(hashParams.get("startDate")).toDate()
      : moment().startOf("day").toDate();
    const ed = hashParams.get("endDate")
      ? moment(hashParams.get("endDate")).toDate()
      : moment().endOf("day").toDate();
    const sr = hashParams.get("selectedRange") || "Today";

    setDateRange([sd, ed]);
    setSelectedRange(sr);
    fetchActions({ startDate: sd, endDate: ed });
  }, []);

  useEffect(() => {
    // keep URL up-to-date for shareable links
    const queryParams = new URLSearchParams({
      startDate: startDate ? moment(startDate).format("YYYY-MM-DD") : "",
      endDate: endDate ? moment(endDate).format("YYYY-MM-DD") : "",
      selectedRange,
    }).toString();
    window.location.hash = queryParams;
  }, [startDate, endDate, selectedRange]);

  const requestSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  const sortedRows = useMemo(() => {
    // Enrich with patient full name and numeric totals
    let sortable = actionsReport.map((item) => {
      const planned = Number(item.planned) || 0;
      const completed = Number(item.completed) || 0;
      return {
        ...item,
        patient: `${item.first_name || ""} ${item.last_name || ""}`.trim(),
        planned,
        completed,
        totalValue: planned + completed,
      };
    });

    if (sortConfig.key !== null) {
      sortable.sort((a, b) => {
        let va;
        let vb;

        if (sortConfig.key === "starttime" || sortConfig.key === "created") {
          va = a[sortConfig.key] ? new Date(a[sortConfig.key]) : null;
          vb = b[sortConfig.key] ? new Date(b[sortConfig.key]) : null;
        } else {
          va = a[sortConfig.key];
          vb = b[sortConfig.key];
        }

        if (va === undefined || va === null) va = "";
        if (vb === undefined || vb === null) vb = "";

        // Numeric compare
        if (typeof va === "number" && typeof vb === "number") {
          if (va < vb) return sortConfig.direction === "ascending" ? -1 : 1;
          if (va > vb) return sortConfig.direction === "ascending" ? 1 : -1;
          return 0;
        }

        // String compare
        if (typeof va === "string" && typeof vb === "string") {
          const cmp = va.localeCompare(vb);
          if (cmp < 0) return sortConfig.direction === "ascending" ? -1 : 1;
          if (cmp > 0) return sortConfig.direction === "ascending" ? 1 : -1;
          return 0;
        }

        // Date / fallback compare
        if (va < vb) return sortConfig.direction === "ascending" ? -1 : 1;
        if (va > vb) return sortConfig.direction === "ascending" ? 1 : -1;
        return 0;
      });
    }
    return sortable;
  }, [actionsReport, sortConfig]);

  // Totals for footer
  const { totalPlanned, totalCompleted, totalOverall } = useMemo(() => {
    let plannedSum = 0;
    let completedSum = 0;
    actionsReport.forEach((row) => {
      plannedSum += Number(row.planned) || 0;
      completedSum += Number(row.completed) || 0;
    });
    return {
      totalPlanned: plannedSum,
      totalCompleted: completedSum,
      totalOverall: plannedSum + completedSum,
    };
  }, [actionsReport]);

  const handleDateRangeSelection = (value) => {
    setSelectedRange(value);
    let s, e;
    switch (value) {
      case "Today":
        s = moment().startOf("day");
        e = moment().endOf("day");
        break;
      case "Yesterday":
        s = moment().subtract(1, "days").startOf("day");
        e = moment().subtract(1, "days").endOf("day");
        break;
      case "Last 7 days":
        s = moment().subtract(6, "days").startOf("day");
        e = moment().endOf("day");
        break;
      case "Last 4 weeks":
        s = moment().subtract(4, "weeks").startOf("day");
        e = moment().endOf("day");
        break;
      case "Month to Date":
        s = moment().startOf("month");
        e = moment().endOf("day");
        break;
      case "Quarter to date":
        s = moment().startOf("quarter");
        e = moment().endOf("day");
        break;
      case "Year to date":
        s = moment().startOf("year");
        e = moment().endOf("day");
        break;
      case "Custom":
      default:
        s = null;
        e = null;
    }
    setDateRange([s ? s.toDate() : null, e ? e.toDate() : null]);
  };

  const renderCustomHeader = ({
    date,
    decreaseMonth,
    increaseMonth,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled,
  }) => (
    <Flex direction="column" width="100%" paddingX="20px">
      <Flex justifyContent="center" alignItems="center" mt={4}>
        <Box
          p={2}
          borderRadius="md"
          borderWidth="2px"
          borderColor="purple.500"
          mr={2}
        >
          {startDate ? startDate.toLocaleDateString() : "Start Date"}
        </Box>
        <Text mx={2}>→</Text>
        <Box
          p={2}
          borderRadius="md"
          borderWidth="2px"
          borderColor="purple.500"
        >
          {endDate ? endDate.toLocaleDateString() : "End Date"}
        </Box>
      </Flex>
      <Flex justifyContent="space-between" alignItems="center" pt={2}>
        <Box
          as="button"
          onClick={decreaseMonth}
          disabled={prevMonthButtonDisabled}
        >
          <FontAwesomeIcon icon={faChevronLeft} />
        </Box>
        <Box>
          {date.toLocaleString("default", { month: "long", year: "numeric" })}
        </Box>
        <Box
          as="button"
          onClick={increaseMonth}
          disabled={nextMonthButtonDisabled}
        >
          <FontAwesomeIcon icon={faChevronRight} />
        </Box>
      </Flex>
    </Flex>
  );

  const hoverBgColor =
    colorMode === "light" ? theme.colors.gray[50] : theme.colors.gray[700];

  const renderType = (t) => {
    if (t === "vam") return "Text message";
    if (t === "vav") return "Phone call";
    return t || "-";
  };

  const renderAction = (a) => {
    if (a === "reschedule_appointment") return "Reschedule";
    if (a === "book_appointment") return "Schedule";
    return a || "-";
  };

  if (isLoading) {
    return (
      <Flex
        justifyContent="center"
        alignItems="center"
        height="100vh"
        width="100%"
        position="relative"
      >
        <Spinner color="blue.500" size="xl" />
      </Flex>
    );
  }

  return (
    <Box p={4} overflowY="auto" maxHeight="100vh" width="100%">
      {/* Control Row */}
      <Flex mb={4} align="center">
        {/* Date Range Quick Select */}
        <Select
          placeholder="Select range"
          value={selectedRange}
          onChange={(e) => handleDateRangeSelection(e.target.value)}
          width="200px"
          mr={2}
        >
          <option value="Today">Today</option>
          <option value="Yesterday">Yesterday</option>
          <option value="Last 7 days">Last 7 days</option>
          <option value="Last 4 weeks">Last 4 weeks</option>
          <option value="Month to Date">Month to Date</option>
          <option value="Quarter to date">Quarter to date</option>
          <option value="Year to date">Year to date</option>
          <option value="Custom">Custom</option>
        </Select>

        {/* Date Picker */}
        <DatePicker
          className={`custom-datepicker ${
            colorMode === "light" ? "light-theme" : "dark-theme"
          }`}
          selectsRange
          startDate={startDate}
          endDate={endDate}
          onChange={(update) => {
            setDateRange(update);
            setSelectedRange("Custom");
          }}
          isClearable
          renderCustomHeader={renderCustomHeader}
        />

        {/* Spacer to push Show Actions to the far right */}
        <Box ml="auto">
          <Button
            leftIcon={<FontAwesomeIcon icon={faMagnifyingGlassDollar} />}
            onClick={() => fetchActions({ startDate, endDate })}
          >
            Show Revenue
          </Button>
        </Box>
      </Flex>

      {/* Report Table */}
      <VStack spacing={0} align="stretch">
        {/* Header Row */}
        <Flex borderBottom="1px" borderColor="gray.200" p={2} fontWeight="bold">
          <HStack spacing={8} flex="1">
            <Text
              flexBasis="12.5%"
              cursor="pointer"
              onClick={() => requestSort("created")}
            >
              Action Date
            </Text>
            <Text
              flexBasis="12.5%"
              cursor="pointer"
              onClick={() => requestSort("starttime")}
            >
              Appointment Date
            </Text>
            <Text
              flexBasis="12.5%"
              cursor="pointer"
              onClick={() => requestSort("patient")}
            >
              Patient
            </Text>
            <Text
              flexBasis="12.5%"
              cursor="pointer"
              onClick={() => requestSort("planned")}
            >
              Planned
            </Text>
            <Text
              flexBasis="12.5%"
              cursor="pointer"
              onClick={() => requestSort("completed")}
            >
              Completed
            </Text>
            <Text
              flexBasis="12.5%"
              cursor="pointer"
              onClick={() => requestSort("totalValue")}
            >
              Total
            </Text>
            <Text
              flexBasis="12.5%"
              cursor="pointer"
              onClick={() => requestSort("type")}
            >
              Type
            </Text>
            <Text
              flexBasis="12.5%"
              cursor="pointer"
              onClick={() => requestSort("action")}
            >
              Action
            </Text>
          </HStack>
        </Flex>

        {/* Data Rows */}
        {sortedRows.map((row, index) => {
          const key =
            row._id ||
            `${row.patient_guid || "none"}-${row.created || "none"}-${
              row.starttime || "none"
            }-${index}`;

          const plannedValue = Number(row.planned) || 0;
          const completedValue = Number(row.completed) || 0;
          const totalValue = plannedValue + completedValue;

          return (
            <Flex key={key} p={2} _hover={{ backgroundColor: hoverBgColor }}>
              <HStack spacing={8} flex="1">
                <Text flexBasis="12.5%" textAlign="left">
                  {row.created
                    ? moment(row.created).format("MMM D, YYYY h:mm a")
                    : "-"}
                </Text>
                <Text flexBasis="12.5%" textAlign="left">
                  {row.starttime
                    ? moment(row.starttime).format("MMM D, YYYY h:mm a")
                    : "-"}
                </Text>
                <Text flexBasis="12.5%" textAlign="left">
                  {row.patient}
                </Text>
                <Text flexBasis="12.5%" textAlign="left">
                  {plannedValue.toLocaleString("en-US", {
                    style: "currency",
                    currency: "USD",
                  })}
                </Text>
                <Text flexBasis="12.5%" textAlign="left">
                  {completedValue.toLocaleString("en-US", {
                    style: "currency",
                    currency: "USD",
                  })}
                </Text>
                <Text flexBasis="12.5%" textAlign="left">
                  {totalValue.toLocaleString("en-US", {
                    style: "currency",
                    currency: "USD",
                  })}
                </Text>
                <Text flexBasis="12.5%" textAlign="left">
                  {renderType(row.type)}
                </Text>
                <Text flexBasis="12.5%" textAlign="left">
                  {renderAction(row.action)}
                </Text>
              </HStack>
            </Flex>
          );
        })}

        {/* TOTAL ROW AT BOTTOM */}
        <Flex
          mt={2}
          pt={2}
          borderTop="2px"
          borderColor="gray.300"
          fontWeight="bold"
        >
          <HStack spacing={8} flex="1">
            <Text flexBasis="12.5%" />
            <Text flexBasis="12.5%" />
            <Text flexBasis="12.5%" textAlign="right">
              Totals:
            </Text>
            <Text flexBasis="12.5%" textAlign="left">
              {totalPlanned.toLocaleString("en-US", {
                style: "currency",
                currency: "USD",
              })}
            </Text>
            <Text flexBasis="12.5%" textAlign="left">
              {totalCompleted.toLocaleString("en-US", {
                style: "currency",
                currency: "USD",
              })}
            </Text>
            <Text flexBasis="12.5%" textAlign="left">
              {totalOverall.toLocaleString("en-US", {
                style: "currency",
                currency: "USD",
              })}
            </Text>
            <Text flexBasis="12.5%" />
            <Text flexBasis="12.5%" />
          </HStack>
        </Flex>
      </VStack>
    </Box>
  );
}

export default AiProductionReport;
